import { useMemo, useState } from 'react';
import { formatDate } from '../utils';

export default function DashboardDeadlines({ projects, onNavigate }) {
  const [showAll, setShowAll] = useState(false);

  const deadlines = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fourteenDaysOut = new Date(today);
    fourteenDaysOut.setDate(fourteenDaysOut.getDate() + 14);
    const items = [];

    for (const p of projects) {
      for (const t of (p.tasks || [])) {
        if (t.isPhaseHeader || t.status === "Complete") continue;
        if (!t.dueDate) continue;
        const due = new Date(t.dueDate);
        due.setHours(0, 0, 0, 0);
        if (due <= fourteenDaysOut) {
          const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
          items.push({
            taskId: t.id,
            taskName: t.name,
            projectId: p.id,
            projectName: p.name,
            dueDate: t.dueDate,
            diffDays,
          });
        }
      }
    }
    items.sort((a, b) => a.diffDays - b.diffDays);
    return items;
  }, [projects]);

  if (deadlines.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Upcoming Deadlines</h3>
        <p className="text-xs text-slate-400">No tasks due within the next 14 days.</p>
      </div>
    );
  }

  const visible = showAll ? deadlines : deadlines.slice(0, 5);

  function getUrgencyBadge(diffDays) {
    if (diffDays < 0) {
      return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{Math.abs(diffDays)}d overdue</span>;
    }
    if (diffDays === 0) {
      return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Today</span>;
    }
    if (diffDays === 1) {
      return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Tomorrow</span>;
    }
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{diffDays} days</span>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-stone-800 mb-3">Upcoming Deadlines</h3>
      <div className="divide-y divide-slate-100">
        {visible.map(dl => (
          <button key={`${dl.projectId}-${dl.taskId}`}
            onClick={() => onNavigate("task-detail", dl.projectId, dl.taskId)}
            className="flex items-center justify-between py-2.5 -mx-2 px-2 rounded-lg w-full text-left cursor-pointer hover:bg-slate-50 transition-colors group">
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-800 truncate">{dl.taskName}</p>
              <p className="text-xs text-slate-400">{dl.projectName}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline">→ Task Detail</span>
              <span className="text-xs text-slate-500">{formatDate(dl.dueDate, { month: "short", day: "numeric" })}</span>
              {getUrgencyBadge(dl.diffDays)}
            </div>
          </button>
        ))}
      </div>
      {deadlines.length > 5 && !showAll && (
        <button onClick={() => setShowAll(true)}
          className="text-xs text-amber-600 font-medium mt-3 hover:text-amber-700">
          View all {deadlines.length} upcoming deadlines →
        </button>
      )}
    </div>
  );
}
